import { loadStripe, Stripe } from '@stripe/stripe-js';

let stripePromise: Promise<Stripe | null>;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
};

// ---------------------------------------------------------------------------
// Checkout Session Creation (DEV ONLY)
// TODO: Move this to a backend endpoint before production.
// Using the secret key client-side is acceptable for development/testing only.
// ---------------------------------------------------------------------------

export interface CreateCheckoutSessionParams {
  planName: string;
  unitAmountCents: number;
  billingInterval: 'month' | 'year';
  customerEmail: string;
  currency?: string;
  metadata?: Record<string, string>;
}

export interface StripeCheckoutSession {
  id: string;
  url: string;
}

export async function createCheckoutSession(
  params: CreateCheckoutSessionParams
): Promise<StripeCheckoutSession> {
  const secretKey = process.env.REACT_APP_STRIPE_SECRET_KEY;
  if (!secretKey) {
    throw new Error('REACT_APP_STRIPE_SECRET_KEY is not configured');
  }

  const origin = window.location.origin;
  const currency = params.currency ?? 'usd';

  const body = new URLSearchParams();
  body.append('mode', 'subscription');
  body.append('customer_email', params.customerEmail);
  body.append('line_items[0][price_data][currency]', currency);
  body.append('line_items[0][price_data][product_data][name]', params.planName);
  body.append('line_items[0][price_data][recurring][interval]', params.billingInterval);
  body.append('line_items[0][price_data][unit_amount]', String(params.unitAmountCents));
  body.append('line_items[0][quantity]', '1');
  body.append('success_url', `${origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`);
  body.append('cancel_url', `${origin}/subscription`);

  if (params.metadata) {
    for (const [key, value] of Object.entries(params.metadata)) {
      body.append(`metadata[${key}]`, value);
    }
  }

  const response = await fetch('https://api.stripe.com/v1/checkout/sessions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${secretKey}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message =
      errorData?.error?.message ?? `Stripe API error (${response.status})`;
    throw new Error(message);
  }

  const session = await response.json();
  if (!session.url) {
    throw new Error('Stripe did not return a checkout URL');
  }

  return { id: session.id, url: session.url };
}
